import React, { useState, useEffect } from "react";
import {
	XMarkIcon,
	PencilIcon,
	TrashIcon,
	EyeIcon,
} from "@heroicons/react/24/outline";
import { useManagementStore } from "../../../store/managementStore";

// 일정 상세 모달 컴포넌트
const ScheduleDetailModal = ({
	isOpen,
	onClose,
	scheduleData,
	onRegister,
	onRefresh,
	onEdit,
}) => {
	const [loading, setLoading] = useState(false);
	const [scheduleTypes, setScheduleTypes] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [previewImage, setPreviewImage] = useState(null);
	const [showImagePreview, setShowImagePreview] = useState(false);
	const itemsPerPage = 10;

	// 모달이 열릴 때 데이터 처리
	useEffect(() => {
		if (isOpen && scheduleData) {
			const schedules = scheduleData.schedules || [];
			if (schedules.length > 0) {
				setScheduleTypes([...schedules]);
				setCurrentPage(1);
			} else {
				setScheduleTypes([]);
			}
		}
	}, [isOpen, scheduleData]);

	// 페이지 변경 핸들러
	const handlePageChange = (pageNumber) => {
		setCurrentPage(pageNumber);
	};

	// 현재 페이지에 표시할 아이템 계산
	const indexOfLastItem = currentPage * itemsPerPage;
	const indexOfFirstItem = indexOfLastItem - itemsPerPage;
	const currentItems = scheduleTypes.slice(indexOfFirstItem, indexOfLastItem);
	const totalPages = Math.ceil(scheduleTypes.length / itemsPerPage);

	// 상세 보기 핸들러 - 이미지 미리보기
	const handleViewDetail = (index) => {
		const rawUrl = currentItems[index].s3_url;
		if (!rawUrl) {
			alert("이미지가 없습니다.");
			return;
		}

		// 1) URL을 '/' 기준으로 나누고
		const parts = rawUrl.split("/");
		// 2) 마지막 segment = 파일명 추출
		const fileName = parts.pop();
		// 3) 파일명만 퍼센트 인코딩
		const encodedFileName = encodeURIComponent(fileName);
		// 4) 다시 합쳐서 최종 URL 생성
		const fixedUrl = [...parts, encodedFileName].join("/");

		// 미리보기용 상태 업데이트
		setPreviewImage(fixedUrl);
		setShowImagePreview(true);
	};

	// 이미지 미리보기 닫기
	const closeImagePreview = () => {
		setShowImagePreview(false);
		setPreviewImage(null);
	};

	// 수정 핸들러
	const handleEdit = (index) => {
		const selectedSchedule = currentItems[index];

		// 수정 모달 열기
		if (onEdit && typeof onEdit === "function") {
			onEdit(selectedSchedule, scheduleData);
		}
	};

	// 삭제 핸들러
	const deleteSchedule = useManagementStore((state) => state.deleteSchedule);

	const handleDelete = (index) => {
		if (window.confirm("일정을 삭제하시겠습니까?")) {
			setLoading(true);

			const scheduleId = currentItems[index].id || currentItems[index]._id;

			deleteSchedule(scheduleId)
				.then((response) => {
					if (response.success) {
						alert("일정 삭제를 성공하였습니다.");
						// onClose(); // 모달 닫기

						// 데이터 새로고침 함수 호출
						if (typeof onRefresh === "function") {
							onRefresh(); // 데이터 새로고침만 수행
						}
					} else {
						alert(
							`일정 삭제 실패: ${
								response.error ||
								"알 수 없는 오류가 발생했습니다."
							}`
						);
					}
				})
				.catch((error) => {
					console.error("일정 삭제 중 오류 발생:", error);
					alert("일정 삭제 중 오류가 발생했습니다.");
				})
				.finally(() => {
					setLoading(false);
				});
		}
	};

	// 모달이 닫혀 있으면 렌더링하지 않음
	if (!isOpen) return null;

	return (
		<>
			{/* 이미지 미리보기 모달 */}
			<ImagePreviewModal
				imageUrl={previewImage}
				isOpen={showImagePreview}
				onClose={closeImagePreview}
			/>

			<div className="fixed inset-0 z-50 overflow-y-auto">
				<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
					{/* 배경 오버레이 */}
					<div
						className="fixed inset-0 transition-opacity"
						aria-hidden="true"
					>
						<div
							className="absolute inset-0 bg-gray-500 opacity-75"
							onClick={onClose}
						></div>
					</div>

					<span
						className="hidden sm:inline-block sm:align-middle sm:h-screen"
						aria-hidden="true"
					>
						&#8203;
					</span>

					{/* 모달 내용 */}
					<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full">
						{/* 모달 헤더 */}
						<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
							<div className="flex justify-between items-center pb-3 border-b mb-4">
								<h3 className="text-lg font-semibold text-gray-900">
									일정 조회
								</h3>
								<button
									onClick={onClose}
									className="text-gray-400 hover:text-gray-500 focus:outline-none"
								>
									<XMarkIcon className="h-6 w-6" />
								</button>
							</div>

							{/* 안내 메시지 */}
							<div
								className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded relative"
								role="alert"
							>
								<div className="flex">
									<div className="py-1">
										<svg
											className="fill-current h-6 w-6 text-blue-500 mr-4"
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 20 20"
										>
											<path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z" />
										</svg>
									</div>
									<div>
										<p className="font-bold">
											여행 일정 타입을 조회합니다.
										</p>
										<p className="text-sm">
											여행 일정별 타입 정보를 조회하는
											페이지 입니다.
										</p>
									</div>
								</div>
							</div>

							{/* 테이블 영역 */}
							<div className="overflow-x-auto">
								<table className="min-w-full divide-y divide-gray-200">
									<thead className="bg-gray-50">
										<tr>
											<th
												scope="col"
												className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
											>
												번호
											</th>
											<th
												scope="col"
												className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
											>
												일정명
											</th>
											<th
												scope="col"
												className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
											>
												가이드 이름
											</th>
											<th
												scope="col"
												className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
											>
												가이드 전화번호
											</th>
											<th
												scope="col"
												className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
											>
												미팅장소
											</th>
											<th
												scope="col"
												className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
											>
												관리
											</th>
										</tr>
									</thead>
									<tbody className="bg-white divide-y divide-gray-200">
										{currentItems.length > 0 ? (
											currentItems.map((item, index) => (
												<tr key={index}>
													<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
														{indexOfFirstItem +
															index +
															1}
													</td>
													<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
														{item.name}
													</td>
													<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
														{item.guide_name}
													</td>
													<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
														{item.guide_phone}
													</td>
													<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
														{item.meeting_place}
													</td>
													<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
														<div className="flex justify-center space-x-2">
															<button
																onClick={() =>
																	handleViewDetail(
																		index
																	)
																}
																className="bg-blue-600 hover:bg-blue-700 text-white py-1 px-2 rounded-md text-sm"
																title="일정 보기"
															>
																일정 보기
															</button>
															<button
																onClick={() =>
																	handleEdit(
																		index
																	)
																}
																className="bg-white hover:bg-gray-100 text-blue-600 border border-blue-600 py-1 px-2 rounded-md text-sm"
																title="수정"
															>
																수정
															</button>
															<button
																onClick={() =>
																	handleDelete(
																		index
																	)
																}
																className="bg-white hover:bg-gray-100 text-red-600 border border-red-600 py-1 px-2 rounded-md text-sm"
																title="삭제"
															>
																삭제
															</button>
														</div>
													</td>
												</tr>
											))
										) : (
											<tr>
												<td
													colSpan="6"
													className="px-6 py-4 text-center text-sm text-gray-500"
												>
													{loading
														? "데이터를 불러오는 중..."
														: "일정 데이터가 없습니다."}
												</td>
											</tr>
										)}
									</tbody>
								</table>

								{/* 페이지네이션 */}
								{totalPages > 1 && (
									<div className="px-4 py-3 pt-6 flex items-center justify-between border-t border-gray-200 sm:px-6">
										<div className="flex-1 flex justify-center">
											<nav
												className="relative z-0 inline-flex rounded-md shadow-sm space-x-4"
												aria-label="Pagination"
											>
												<button
													onClick={() =>
														handlePageChange(
															currentPage - 1
														)
													}
													disabled={currentPage === 1}
													className={`relative inline-flex items-center px-2 py-2 rounded-l-md bg-white text-sm font-medium ${
														currentPage === 1
															? "text-gray-300 cursor-not-allowed"
															: "text-gray-500 hover:bg-gray-50"
													}`}
												>
													이전
												</button>

												{Array.from(
													{ length: totalPages },
													(_, i) => i + 1
												).map((number) => (
													<button
														key={number}
														onClick={() =>
															handlePageChange(
																number
															)
														}
														className={`relative inline-flex items-center px-4 py-2 mr-2 border border-gray-300 bg-white text-sm font-medium ${
															currentPage ===
															number
																? "bg-blue-50 text-blue-600"
																: "text-gray-700 hover:bg-gray-50"
														}`}
													>
														{number}
													</button>
												))}

												<button
													onClick={() =>
														handlePageChange(
															currentPage + 1
														)
													}
													disabled={
														currentPage ===
														totalPages
													}
													className={`relative inline-flex items-center px-2 py-2 ml-2 rounded-r-md bg-white text-sm font-medium ${
														currentPage ===
														totalPages
															? "text-gray-300 cursor-not-allowed"
															: "text-gray-500 hover:bg-gray-50"
													}`}
												>
													다음
												</button>
											</nav>
										</div>
									</div>
								)}
							</div>
						</div>

						{/* 모달 푸터 */}
						<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
							<button
								type="button"
								className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
								onClick={onClose}
							>
								닫기
							</button>
							{/* 신규 등록 버튼 */}
							<button
								className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm"
								onClick={() =>
									onRegister && onRegister(scheduleData)
								}
							>
								신규 등록
							</button>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

// 이미지 미리보기 모달 컴포넌트
const ImagePreviewModal = ({ imageUrl, isOpen, onClose }) => {
	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-[60] overflow-y-auto">
			<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
				{/* 배경 오버레이 */}
				<div
					className="fixed inset-0 transition-opacity"
					aria-hidden="true"
				>
					<div
						className="absolute inset-0 bg-gray-800 opacity-75"
						onClick={onClose}
					></div>
				</div>

				<span
					className="hidden sm:inline-block sm:align-middle sm:h-screen"
					aria-hidden="true"
				>
					&#8203;
				</span>

				{/* 모달 내용 */}
				<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
					{/* 모달 헤더 */}
					<div className="bg-white px-4 pt-5 pb-4 sm:p-6">
						<div className="flex justify-between items-center pb-3 border-b mb-4">
							<h3 className="text-lg font-semibold text-gray-900">
								이미지 미리보기
							</h3>
							<button
								onClick={onClose}
								className="text-gray-400 hover:text-gray-500 focus:outline-none"
							>
								<XMarkIcon className="h-6 w-6" />
							</button>
						</div>

						{/* 이미지 영역 - 가로 너비 고정, 세로 스크롤 */}
						<div
							className="flex justify-center items-start p-4 overflow-y-auto"
							style={{ maxHeight: "80vh" }}
						>
							<div className="w-full flex justify-center">
								<img
									src={imageUrl}
									alt="일정 이미지"
									className="w-auto max-w-full h-auto"
									style={{ maxWidth: "800px" }}
									onError={(e) => {
										e.target.style.display = "none";
										e.target.nextElementSibling.style.display =
											"block";
									}}
								/>
								<div className="text-gray-500 text-lg p-10 border border-gray-300 rounded-md hidden">
									이미지를 불러올 수 없습니다.
								</div>
							</div>
						</div>
					</div>

					{/* 모달 푸터 */}
					<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
						<button
							type="button"
							className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
							onClick={onClose}
						>
							닫기
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ScheduleDetailModal;
